import { useEffect, useMemo, useState } from 'react';

import {
  getMySessionPipelines,
  getTenantSessionPipelines,
  type SessionPipeline,
} from '../api/sessionPipeline';
import { useInsightsPolling } from './useInsightsPolling';

/** The API answers at most this many ids per call. */
const BATCH = 200;

/**
 * Pipeline status for a set of sessions, keyed by `session_id`, refreshed
 * while any of them is still on its way in.
 *
 * `groups` pairs a tenant with its session ids: a tenant admin passes one
 * group; a participant whose sessions span tenants passes one per tenant,
 * since their endpoint is scoped to a tenant. `mode` picks the endpoint.
 *
 * Refreshes every 15 s while something is in progress, for up to an hour (a
 * long case's clips can take that long to come off the edge box), pausing in
 * a background tab like every other poller here. Failures are silent — the
 * strip and dots are an addition to the page, never a reason for it to break.
 */
export function useSessionPipelines(
  mode: 'tenant' | 'me',
  groups: { tenantId: string; sessionIds: string[] }[],
): Map<string, SessionPipeline> {
  const [byId, setById] = useState<Map<string, SessionPipeline>>(new Map());
  // A stable key, so a re-render with the same ids doesn't refetch.
  const key = useMemo(
    () =>
      JSON.stringify(
        groups
          .filter((g) => g.sessionIds.length > 0)
          .map((g) => [g.tenantId, [...g.sessionIds].sort()]),
      ),
    [groups],
  );

  const load = useMemo(() => {
    const parsed = JSON.parse(key) as [string, string[]][];
    return async (): Promise<Map<string, SessionPipeline>> => {
      const calls: Promise<SessionPipeline[]>[] = [];
      for (const [tenantId, ids] of parsed) {
        for (let i = 0; i < ids.length; i += BATCH) {
          const chunk = ids.slice(i, i + BATCH);
          calls.push(
            mode === 'tenant'
              ? getTenantSessionPipelines(tenantId, chunk)
              : getMySessionPipelines(tenantId, chunk),
          );
        }
      }
      const results = await Promise.all(calls);
      return new Map(results.flat().map((p) => [p.session_id, p]));
    };
  }, [key, mode]);

  useEffect(() => {
    let alive = true;
    setById(new Map());
    load()
      .then((m) => alive && setById(m))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [load]);

  const inProgress = useMemo(() => [...byId.values()].some(stillMoving), [byId]);
  useInsightsPolling({
    pending: inProgress,
    intervalMs: 15_000,
    maxAttempts: 240,
    onTick: () => {
      load()
        .then(setById)
        .catch(() => {});
    },
  });

  return byId;
}

/**
 * Whether a pipeline can still change on its own, so the strip keeps polling
 * (review PIPE-1).
 *
 * It polled only while `overall` was 'in_progress'. But a stalled upload turns
 * its step into a warning, the warning wins `overall`, and polling stopped —
 * so an upload that resumed (Wi-Fi back, the phone reopened) stayed "stalled"
 * on screen until a reload, and any other step still running was hidden
 * behind it too. A warning on an upload step can clear itself; keep watching.
 */
export function stillMoving(p: SessionPipeline): boolean {
  if (p.overall === 'in_progress') return true;
  return p.steps.some(
    (s) =>
      s.state === 'active' ||
      s.state === 'waiting' ||
      (s.state === 'warning' && (s.key === 'sensor' || s.key === 'video')),
  );
}

